import { useMemo, useContext, useState } from 'react';
import FlexibleLink from '../../../util/FlexibleLink';
import { createColumnHelper, Row, ColumnDef } from '@tanstack/react-table';
import { AdminOrganizations } from '@platform/client';
import { AdminOrganization, VettingStatus } from '@platform/client/schemas';
import { keepPreviousData } from '@tanstack/react-query';
import { Icon, PillBadge, PillBadgeColors } from '@platform/components';
import { getDaysSince } from '../../../util/DateHelpers';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';
import { VettingModal } from './OrganizationVetting';
import { Link } from 'wouter';

export const OrganizationsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Organizations'}>
      <OrganizationsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<AdminOrganization>();

export const OrganizationsAdminContent = () => {
  const { adminQueryOptions } = useContext(AdminTableContext);
  const [vettingModalOrg, setVettingModalOrg] = useState<AdminOrganization | undefined>();

  const openVettingModal = (row: AdminOrganization) => {
    setVettingModalOrg(row);
  };

  const closeVettingModal = () => {
    setVettingModalOrg(undefined);
  };

  const { data, isSuccess, isLoading, isError } = AdminOrganizations.useGetOrganizations(
    adminQueryOptions,
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { organizations = [], filteredCount = 0 } = data || {};
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'Id',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => {
            return (
              <FlexibleLink
                type='legacy'
                href={`/organization/${row.original.id}`}
                className='text-brand-500 hover:underline'
              >
                {row.original.name}
              </FlexibleLink>
            );
          },
          meta: {
            filterVariant: 'text',
          },
          size: 300,
        }),
        columnHelper.accessor('projects', {
          header: 'Projects',
          cell: (projects) => {
            // add a tooltip for full project name
            return (
              <div className='flex flex-col text-wrap'>
                {projects.getValue()?.map((project) => {
                  return (
                    <FlexibleLink
                      type='legacy'
                      href={`/admin/browse/project/${project.id}`}
                      className='text-brand-500 hover:underline'
                      key={project.id}
                    >
                      {`${project.name.slice(0, 41)} ... [${project.id}]`}
                    </FlexibleLink>
                  );
                })}
              </div>
            );
          },
          enableColumnFilter: false,
          enableSorting: false,
          size: 200,
        }),
        columnHelper.accessor('matchingNetworks', {
          header: 'Matching Networks',
          cell: (matchingNetworks) => {
            return matchingNetworks.getValue()?.map((network) => {
              return (
                <PillBadge variant='plain' color='indigo' key={network} size='sm'>
                  {network}
                </PillBadge>
              );
            });
          },
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('vettingStatus', {
          header: 'Vetting Status',
          cell: ({ row }) => {
            return (
              <div className='flex justify-between'>
                <OrgVettingBadge row={row} />
                <button onClick={() => openVettingModal(row.original)}>
                  <Icon type='edit-01' size='14' />
                </button>
              </div>
            );
          },
          meta: {
            filterVariant: 'text',
          },
          size: 50,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleString(),
          size: 25,
        }),
        columnHelper.display({
          id: 'actions',
          cell: ({ row }) => {
            return (
              <div className='flex flex-col gap-1'>
                <Link
                  className='text-brand-500 hover:underline'
                  to={`/organizations/vetting/${row.original.id}`}
                >
                  Review
                </Link>
                <FlexibleLink
                  href={`/admin/organization/${row.original.id}/edit`}
                  newTab={true}
                  type='legacy'
                  className='text-brand-500 hover:underline'
                >
                  Edit
                </FlexibleLink>
              </div>
            );
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Actions</span>,
          size: 25,
        }),
      ] as ColumnDef<AdminOrganization>[],
    [],
  );

  return (
    <>
      <AdminTable
        data={organizations}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={filteredCount}
        columns={columns}
        enableFiltering={true}
      />
      {vettingModalOrg && (
        <VettingModal
          isOpen={!!vettingModalOrg}
          onClose={closeVettingModal}
          organizationId={vettingModalOrg?.id}
          vettingStatus={vettingModalOrg?.vettingStatus}
          page={adminQueryOptions.page}
          limit={adminQueryOptions.limit}
        />
      )}
    </>
  );
};

const OrgVettingBadge = ({ row }: { row: Row<AdminOrganization> }) => {
  const { vettingStatus, vettingStatusDate } = row.original;
  let badgeColor: PillBadgeColors;
  switch (vettingStatus) {
    case VettingStatus.transform:
      badgeColor = 'success-500';
      break;
    case VettingStatus.match:
      badgeColor = 'success-100';
      break;
    case VettingStatus.rejected:
      badgeColor = 'error';
      break;
    case VettingStatus.pending:
      badgeColor = 'warning';
      break;
    case VettingStatus.signup_incomplete:
      badgeColor = 'warning';
      break;
    default:
      throw new Error(`Unknown vetting status: ${vettingStatus}`);
  }
  return (
    <PillBadge variant='plain' color={badgeColor} size='sm'>
      {vettingStatus.split('_').join(' ').toUpperCase()}
      {vettingStatus === 'pending' && (
        <span className='text-xs text-gray-500 ml-1'>
          ({getDaysSince(vettingStatusDate)} days ago)
        </span>
      )}
    </PillBadge>
  );
};
